/**
 * OQ-316 — ô captcha Cloudflare Turnstile.
 *
 * Nạp script của Cloudflare theo yêu cầu, ngay lúc trang đăng ký cần tới nó.
 *
 * ── Vì sao không nhét thẻ <script> vào index.html ─────────────────────────
 *
 * Captcha chỉ xuất hiện ở một trang, và chỉ khi người vận hành bật nó. Nạp sẵn
 * cho mọi người vào hệ thống nghĩa là mọi lượt tải trang đều kéo thêm một tệp
 * từ tên miền của Cloudflare — kể cả người dùng đã đăng nhập từ lâu, kể cả khi
 * captcha đang tắt.
 */

import { useEffect, useRef, type JSX } from 'react';

/** API mà script Turnstile gắn vào `window`. */
interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
    },
  ) => string;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

function napScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();

  const sanCo = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (sanCo) {
    /* Đang nạp dở từ một lượt render trước — chờ chính nó, đừng nạp thêm bản
       thứ hai. */
    return new Promise((resolve, reject) => {
      sanCo.addEventListener('load', () => resolve());
      sanCo.addEventListener('error', () => reject(new Error('turnstile script')));
    });
  }

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.addEventListener('load', () => resolve());
    s.addEventListener('error', () => reject(new Error('turnstile script')));
    document.head.appendChild(s);
  });
}

/**
 * @param onToken nhận token mỗi lần Cloudflare cấp; nhận `''` khi token hết hạn
 *                hoặc lượt kiểm hỏng — nút gửi phải khoá lại theo.
 */
export function TurnstileWidget({
  siteKey,
  onToken,
}: {
  siteKey: string;
  onToken: (token: string) => void;
}): JSX.Element {
  const hop = useRef<HTMLDivElement | null>(null);
  /* Giữ callback trong ref: nếu để nó trong mảng phụ thuộc, mỗi lượt render của
     trang cha sẽ vẽ lại widget và người dùng phải làm captcha từ đầu. */
  const traVe = useRef(onToken);
  traVe.current = onToken;

  useEffect(() => {
    let song = true;
    let id: string | undefined;

    void napScript()
      .then(() => {
        if (!song || !hop.current || !window.turnstile) return;
        id = window.turnstile.render(hop.current, {
          sitekey: siteKey,
          callback: (token) => traVe.current(token),
          'expired-callback': () => traVe.current(''),
          'error-callback': () => traVe.current(''),
          theme: 'auto',
        });
      })
      .catch(() => {
        /* Không nạp được script (chặn quảng cáo, mạng công ty, Cloudflare sập).
           Trả token rỗng để nút gửi ở lại trạng thái khoá — và máy chủ cũng sẽ
           từ chối, nên hai bên nói cùng một điều. */
        if (song) traVe.current('');
      });

    return () => {
      song = false;
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [siteKey]);

  return <div ref={hop} data-od-id="turnstile-widget" />;
}
